import { useState } from 'react';
import { CalendarDays, MapPin, RotateCcw } from 'lucide-react';
import type { InviteCampaignSettings } from '@peles/shared';
import { EnvelopeReveal } from '../EnvelopeReveal';
import { InvitationPages } from '../InvitationPages';
import { TransparentImage } from '../../common';
import { hebrewDateString } from '../../../utils/hebrewCalendar';

/** The event as an invitation shows it. */
export interface CampaignEventInfo {
  name: string;
  startAt: Date | string;
  endAt?: Date | string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  logoUrl?: string | null;
}

export const DEFAULT_INVITE_ACCENT = '#1a365d';

export interface InvitePreviewProps {
  event?: CampaignEventInfo;
  settings: InviteCampaignSettings;
  message?: string | null;
  /** Artwork page URLs, cover first. */
  pages: string[];
}

function formatDate(startAt: Date | string): string {
  return new Date(startAt).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The invitation as a guest's phone shows it, drawn from the step's unsaved
 * choices so every change is seen at once — the envelope, the artwork, the
 * colours, the heading, the logo. The answer form is sketched, not live.
 */
export function InvitePreview({ event, settings, message, pages }: InvitePreviewProps) {
  // Bumped to close the envelope again and replay it.
  const [replay, setReplay] = useState(0);
  const accent = settings.accentColor || DEFAULT_INVITE_ACCENT;
  const eventName = event?.name ?? 'Your event';
  const headline = settings.envelopeMessage?.trim() || eventName;
  const showEnvelope = settings.showEnvelope !== false;

  return (
    <figure className="mx-auto w-full max-w-[22rem]" aria-label="Invitation preview">
      <div className="rounded-[2.25rem] border-[10px] border-gray-900 bg-gray-900 shadow-xl">
        <div
          className="theme-light h-[34rem] overflow-y-auto rounded-[1.6rem] px-3 py-5"
          style={{ backgroundColor: settings.backgroundColor || '#f9fafb' }}
          data-testid="invite-preview-screen"
        >
          <EnvelopeReveal
            key={replay}
            accentColor={accent}
            envelopeColor={settings.envelopeColor}
            sealColor={settings.sealColor}
            message={headline}
            sealImageUrl={settings.envelopeSealLogo ? event?.logoUrl : null}
            disabled={!showEnvelope}
          >
            <div className="space-y-3">
              {pages.length > 0 && (
                <InvitationPages
                  pages={pages}
                  eventName={eventName}
                  accentColor={accent}
                  autoFlip={false}
                />
              )}
              <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                {settings.includeEventLogo && event?.logoUrl && (
                  <TransparentImage
                    src={event.logoUrl}
                    alt="Event logo"
                    className="mx-auto mb-2 max-h-14 w-auto object-contain"
                  />
                )}
                <p className="font-serif text-xl" style={{ color: accent }}>
                  {headline}
                </p>
                {event && (
                  <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-600">
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                    {formatDate(event.startAt)}
                  </p>
                )}
                {event && settings.showHebrewDate && (
                  <p className="text-xs text-gray-600" dir="rtl">
                    {hebrewDateString(new Date(event.startAt))}
                  </p>
                )}
                {event?.venueName && (
                  <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-gray-600">
                    <MapPin className="h-3.5 w-3.5" aria-hidden />
                    {event.venueName}
                  </p>
                )}
                {message && (
                  <p className="mt-3 whitespace-pre-line text-xs text-gray-700">{message}</p>
                )}
              </div>
              {settings.rsvpEnabled !== false && (
                <div className="rounded-2xl bg-white p-4 shadow-sm" aria-hidden>
                  <p className="text-center text-sm font-medium text-gray-900">Will you attend?</p>
                  <div className="mt-2 grid gap-1.5">
                    {[
                      'Joyfully Accept',
                      ...(settings.allowMaybe === false ? [] : ['Maybe']),
                      'Regretfully Decline',
                    ].map((label, index) => (
                      <span
                        key={label}
                        className="rounded-xl border py-2 text-center text-xs font-medium"
                        style={
                          index === 0
                            ? { borderColor: accent, color: accent }
                            : { borderColor: '#e5e7eb', color: '#374151' }
                        }
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </EnvelopeReveal>
        </div>
      </div>
      <figcaption className="mt-2 flex items-center justify-center gap-3 text-xs text-gray-500">
        As a guest sees it
        {showEnvelope && (
          <button
            type="button"
            onClick={() => setReplay((n) => n + 1)}
            className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-800"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Close the envelope
          </button>
        )}
      </figcaption>
    </figure>
  );
}
